import { ref } from 'vue'

import type { Ref } from 'vue'

import { classifyFiles } from '@shared/ui/other'

import { useSalesTaxRatesImportApi } from '../../../api/tools/sales-tax/import.hook'

/** Extensions the endpoint accepts — kept as extensions, matching the file dialog's `accept`
 *  attribute and `classifyFiles`. Mirrors `App\Constants\File::SALES_TAX_EXTENSIONS_WHITELIST`. */
export const SALES_TAX_ACCEPT: string[] = ['.csv', '.txt']

/** 80 MB — `App\Constants\File::MAX_SIZE_KB` (81920 KB). */
const SALES_TAX_MAX_SIZE = 80 * 1024 * 1024

interface UseImportSalesTaxReturn {
  refFile: Ref<File | null>
  refIsUploading: Ref<boolean>
  refProgress: Ref<number>
  refError: Ref<string | null>
  selectFiles: (files: File[]) => void
  submit: () => void
  reset: () => void
}

export const useImportSalesTax = (): UseImportSalesTaxReturn => {
  const refFile = ref<File | null>(null)
  const refIsUploading = ref<boolean>(false)
  const refProgress = ref<number>(0)
  const refError = ref<string | null>(null)

  const reset = (): void => {
    refFile.value = null
    refProgress.value = 0
    refError.value = null
  }

  // Selecting a file only stages it; the import runs on an explicit `submit()` — the import
  // fully replaces the rate table, so it must be a deliberate action, not a side effect of a drop.
  const selectFiles = (files: File[]): void => {
    if (refIsUploading.value) {
      return
    }

    const file = files[0]
    if (!file) {
      return
    }

    const { accepted, rejected } = classifyFiles([file], {
      accept: SALES_TAX_ACCEPT,
      maxSize: SALES_TAX_MAX_SIZE,
      remainingSlots: 1,
      existingKeys: new Set<string>(),
    })

    const refusal = rejected[0]
    if (refusal) {
      refError.value = refusal.reason
      refFile.value = null
      return
    }

    const accept = accepted[0]
    if (!accept) {
      return
    }

    refError.value = null
    refFile.value = accept.file
  }

  const submit = (): void => {
    const file = refFile.value
    if (!file || refIsUploading.value) {
      return
    }

    void useSalesTaxRatesImportApi({
      file,
      onStart: () => {
        refIsUploading.value = true
        refProgress.value = 0
      },
      onProgress: (percent) => {
        refProgress.value = percent
      },
      onSuccess: () => {
        reset()
      },
      onError: (message) => {
        refError.value = message
      },
      onFinish: () => {
        refIsUploading.value = false
      },
    })
  }

  return {
    refFile,
    refIsUploading,
    refProgress,
    refError,
    selectFiles,
    submit,
    reset,
  }
}
